import { useContext } from 'react';
import { useDispatch } from 'react-redux';

import { Box, IconButton, Menu, ModalTrigger, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconLinkOutline, IconMailFilled, IconMoreFilled, IconShareWebOutline } from '@zillow/constellation-icons';
import { LoadingButton } from '@zg-rentals/loading-components';
import { PERMISSION_REQUEST_TYPE } from '@zg-rentals/rental-manager-env';

import pathsManifest from '../../../../pathsManifest';

import { RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { LoadingMapContext } from '../../../../providers/LoadingMapContext/context';

import useCopyApplicationLinkApi from '../../../../hooks/queries/useCopyApplicationLink';

import ApplicationActions from '../../../../actions/ApplicationActions';
// utils;
import { screenSize as SCREEN_SIZE } from '../../../../utils/browserUtils';
import { copyLinkToClipboard, shareLinkOnMobile } from '../../../../utils/copyLinkUtils';

// constants;
import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';

import type { ApplicationsSummary } from '../../interfaces';

const SHARE_LINK_TEXT = 'Click here to apply to your Zillow Rentals listing!';

type Props = {
  displayType?: VERTICAL_DISPLAY_TYPE;
  hasRecentApplications?: boolean;
  /**
   * an override to displaying view older applications cta
   * this should only be displayed in some contexts as there is an ApplicationsHeader that is used in other contexts
   * ex: EmptyApplicationsCard
   */
  hideViewOlderApplications?: boolean;
  isTertiaryButtonStyling?: boolean;
  permissionType?: string | null;
  applicationsSummary?: ApplicationsSummary;
  onSetApplicationManagerClick?: () => void;
};

// TODO: handle displayType
const ApplicationShareLinks = ({
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
  hasRecentApplications = false,
  hideViewOlderApplications = true,
  isTertiaryButtonStyling = false,
  permissionType = null,
  applicationsSummary,
  onSetApplicationManagerClick,
}: Props) => {
  const { enqueueToast } = useToast();
  const dispatch = useDispatch();

  const constellationScreenSize = useSelector((state) => state.app.constellationScreenSize);
  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const isMobile = useSelector((state) => state.app.isMobile);

  const {
    user: {
      isIdVerified,
      features: { canAccessBusinessVerificationLite },
    },
    properties: { currentProperty },
  } = useSelector((state) => state);

  const {
    loadingByComponent: { ConnectedApplications: isLoading },
  } = useContext(LoadingMapContext);

  const {
    isApplicationsAccepted: applicationsAccepted = false,
    urlPath = '',
    rentalApplicationData,
  }: ApplicationsSummary = (applicationsSummary as ApplicationsSummary) || {};

  const {
    totalNumberOfDeclinedApplications = 0,
    totalNumberOfWithdrawnApplications = 0,
    totalNumberOfActiveApplications = 0,
  } = rentalApplicationData || {};

  const isPermissionTypeRestricted =
    permissionType === PERMISSION_REQUEST_TYPE.REQUEST_ACCESS ||
    permissionType === PERMISSION_REQUEST_TYPE.NO_PERMISSION;

  const isIDVerificationNeeded =
    currentProperty.rentalProtection.isEligible && !isIdVerified && !canAccessBusinessVerificationLite;

  const isPermissionTypeApplicationManager = permissionType === PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER;

  const totalNumberOfAllApplications =
    totalNumberOfDeclinedApplications + totalNumberOfWithdrawnApplications + totalNumberOfActiveApplications;

  const displayViewOlderApplicationsLink =
    !hideViewOlderApplications &&
    ((!isPermissionTypeRestricted && applicationsAccepted && totalNumberOfAllApplications > 0) ||
      isPermissionTypeApplicationManager);

  /**
   * CTA Breakpoint
   *
   * send applications cta and copy link break at the XL breakpoint for now
   * because this component is displayed along with the inquiries component
   * if this component is displayed in isolation the breakpoint will most likely change
   *
   * keep the following breakpoints in sync with the xl breakpoint with renderFullType:
   * look for the comment "sync with cta breakpoint"
   */
  const isScreenSizeGreaterOrEqualToLg =
    constellationScreenSize === SCREEN_SIZE.LG ||
    constellationScreenSize === SCREEN_SIZE.XL ||
    constellationScreenSize === SCREEN_SIZE.XXL;

  const applicationLink =
    typeof window !== 'undefined' ? pathsManifest.getGenericApplicationUrl(window.location.host, listingAlias) : '';

  const { data } = useCopyApplicationLinkApi(listingAlias, applicationLink, {
    initialData: {
      response: applicationLink,
    },
  });

  const copyLink = async () => {
    try {
      // @ts-expect-error
      copyLinkToClipboard(enqueueToast, data?.response);
    } catch {
      enqueueToast(
        <Toast id="share-link-toast-error" tone="critical">
          Unable to copy link. Please try again.
        </Toast>,
        { duration: 10000 },
      );
    }
  };

  const shareLink = async () => {
    try {
      await dispatch(ApplicationActions.copyApplicationLinkApi({ listingAlias }));
      shareLinkOnMobile(
        enqueueToast,
        SHARE_LINK_TEXT,
        pathsManifest.getGenericApplicationUrl(window.location.host, listingAlias),
      );
    } catch {
      enqueueToast(
        <Toast id="share-link-toast-error" tone="critical">
          Unable to share link.
        </Toast>,
        {
          duration: 10000,
        },
      );
    }
  };

  const renderInviteRentersToApplyCTA = () => {
    const buttonLabel = 'Send application link';

    return (
      <>
        {applicationsAccepted || isLoading ? (
          <ModalTrigger multiModalTriggerId="InviteToApplyModal">
            <LoadingButton
              tone={isTertiaryButtonStyling && !isMobile ? 'neutral' : 'brand'}
              emphasis={isTertiaryButtonStyling ? 'outlined' : 'filled'}
              fluid={{ base: true, xl: false }}
              size="md"
              icon={<IconMailFilled />}
              data-testid="invite-to-apply-button"
              isLoading={isLoading}
              loadingWidth="50%"
              css={{
                marginBottom: { base: '200', lg: 0 },
                marginRight: { base: 0, lg: '100 !important' },
                maxWidth: isScreenSizeGreaterOrEqualToLg === true ? 245 : 'unset',
              }}
            >
              {buttonLabel}
            </LoadingButton>
          </ModalTrigger>
        ) : null}
      </>
    );
  };

  const renderWebView = () => {
    return (
      <>
        {applicationsAccepted || isLoading ? (
          <LoadingButton
            aria-label="Copy link"
            tone={isTertiaryButtonStyling ? 'neutral' : 'brand'}
            emphasis="outlined"
            size="md"
            data-testid="copy-link-button"
            fluid={{ base: true, lg: false }}
            css={{
              maxWidth: isScreenSizeGreaterOrEqualToLg === true ? 245 : 'unset',
            }}
            icon={<IconLinkOutline />}
            onClick={copyLink}
            isLoading={isLoading}
            loadingWidth="20%"
          >
            {isScreenSizeGreaterOrEqualToLg === false && <Box css={{ marginLeft: '200' }}>Copy link</Box>}
          </LoadingButton>
        ) : (
          <Menu
            trigger={
              <Menu.Button aria-label="Invite to apply" data-testid="invite-to-apply-dropdown">
                Invite to apply
              </Menu.Button>
            }
            content={
              <>
                <ModalTrigger multiModalTriggerId="InviteToApplyModal">
                  <Menu.Item>Email application</Menu.Item>
                </ModalTrigger>
                <Menu.Item onClick={copyLink}>Copy application link</Menu.Item>
              </>
            }
          />
        )}
      </>
    );
  };

  const renderViewOlderApplicationTextButton = () => {
    const label = 'View older applications';

    if (isPermissionTypeApplicationManager) {
      return (
        <TextButton
          data-testid="ViewOlderApplicationsCTA ViewOlderApplicationsCTA--ApplicationManager"
          asChild
          onClick={onSetApplicationManagerClick}
        >
          <a>{label}</a>
        </TextButton>
      );
    }

    if (isIDVerificationNeeded) {
      return (
        <ModalTrigger multiModalTriggerId="IdVerificationOnboardingModal">
          <TextButton data-testid="ViewOlderApplicationsCTA ViewOlderApplicationsCTA--NeedsIDVerification" asChild>
            {label}
          </TextButton>
        </ModalTrigger>
      );
    }

    return (
      <TextButton asChild data-testid="ViewOlderApplicationsCTA">
        <a href={urlPath}>{label}</a>
      </TextButton>
    );
  };

  const renderFullType = () => {
    return (
      <>
        <Box
          aria-label="invite renters to apply"
          css={{
            display: 'flex',
            // sync with cta breakpoint
            alignItems: { base: 'center', lg: 'unset' },
            justifyContent: { base: 'center', lg: hasRecentApplications ? 'flex-start' : 'center' },
            flexDirection: { base: 'column', lg: 'row' },
          }}
        >
          {renderInviteRentersToApplyCTA()}

          {isMobile && !isLoading ? (
            <Menu
              trigger={
                <IconButton
                  title="More sharing options"
                  tone="brand"
                  emphasis="outlined"
                  data-testid="more-sharing-options-dropdown"
                  icon={<IconMoreFilled />}
                />
              }
              content={
                <>
                  <Menu.Item onClick={copyLink}>
                    <Menu.ItemIcon render={<IconLinkOutline />} css={{ color: 'icon.accent.brand.hero' }} />
                    <Menu.ItemLabel>Copy link</Menu.ItemLabel>
                  </Menu.Item>
                  <Menu.Item onClick={shareLink}>
                    <Menu.ItemIcon render={<IconShareWebOutline />} css={{ color: 'icon.accent.brand.hero' }} />
                    <Menu.ItemLabel>Share with other apps</Menu.ItemLabel>
                  </Menu.Item>
                </>
              }
            />
          ) : (
            renderWebView()
          )}
        </Box>

        {displayViewOlderApplicationsLink && (
          <Box data-testid="ViewOlderApplications-Container" css={{ marginTop: { base: '200', lg: '400' } }}>
            {renderViewOlderApplicationTextButton()}
          </Box>
        )}
      </>
    );
  };

  const renderSimpleType = () => {
    return (
      <ModalTrigger multiModalTriggerId="InviteToApplyModal">
        <TextButton textStyle="body-sm-bold">Invite to apply</TextButton>
      </ModalTrigger>
    );
  };

  return (
    <div data-testid="ApplicationShareLinks">
      {displayType === VERTICAL_DISPLAY_TYPE.FULL && renderFullType()}
      {displayType === VERTICAL_DISPLAY_TYPE.SIMPLE && renderSimpleType()}
    </div>
  );
};

export default ApplicationShareLinks;
